import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";
import { IApiBidirectionalTranslation, IBidirectionalTranslationFormData } from "../../types";
import { INITIAL_BIDIRECTIONAL_TRANSLATION_SETTINGS } from "../configs";
import { mergeKioskSettings } from "../utils/mergeKioskSettings";
import { bidirectionalTranslationValidationSchema } from "../validations";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";

interface UseBidirectionalTranslationSettingsProps {
    characterId: string;
}

const DEFAULT_FORM: IBidirectionalTranslationFormData = {
    enabled: INITIAL_BIDIRECTIONAL_TRANSLATION_SETTINGS.enabled,
};

export const useBidirectionalTranslationSettings = ({ characterId }: UseBidirectionalTranslationSettingsProps) => {
    const { t } = useTranslation();
    const { kioskSetting, isLoadingKioskSetting, isUpdatingKioskSetting, updateSettings } = useKioskSettings({
        characterId,
    });

    // ==================== Form State ====================
    const methods = useForm<IBidirectionalTranslationFormData>({
        defaultValues: DEFAULT_FORM,
        resolver: yupResolver(bidirectionalTranslationValidationSchema()),
        mode: "onChange",
    });

    const { control, handleSubmit, formState, reset, setValue } = methods;

    const watchedEnabled = useWatch({ control, name: "enabled" });

    // ==================== Toggle Handler ====================
    const isEnabled = watchedEnabled;
    const handleToggle = (enabled: boolean) => {
        setValue("enabled", enabled, { shouldDirty: true });
    };

    // ==================== Data Transformation ====================
    const transformAPItoForm = useCallback(
        (apiData: IApiBidirectionalTranslation | undefined): IBidirectionalTranslationFormData => {
            if (!apiData) {
                return DEFAULT_FORM;
            }
            return {
                enabled: apiData.enabled ?? false,
            };
        },
        []
    );

    const transformFormToAPI = useCallback(
        (formData: IBidirectionalTranslationFormData): IApiBidirectionalTranslation => {
            return {
                enabled: formData.enabled,
            };
        },
        []
    );

    // ==================== Submit Handler ====================
    const onSubmit = async (data: IBidirectionalTranslationFormData) => {
        try {
            const apiPayload = transformFormToAPI(data);
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                bidirectional_translation: apiPayload,
            });

            await updateSettings({ native: mergedNative });

            // Reset form with new values to clear dirty state
            reset(data);
            customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");
        } catch (error) {
            customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
        }
    };

    const saveHandler = async () => {
        await handleSubmit(onSubmit)();
    };

    useTabGuard("bidirectional_translation", formState.isDirty, saveHandler);

    // ==================== Initialization ====================
    useEffect(() => {
        if (!isLoadingKioskSetting) {
            const translationData = kioskSetting?.data?.native?.bidirectional_translation;
            const formData = transformAPItoForm(translationData ?? INITIAL_BIDIRECTIONAL_TRANSLATION_SETTINGS);
            reset(formData);
        }
    }, [isLoadingKioskSetting, kioskSetting?.data?.native?.bidirectional_translation, reset, transformAPItoForm]);

    return {
        // 表單方法
        methods,
        handleSubmit,
        onSubmit,
        formState,

        // Toggle state
        isEnabled,
        handleToggle,

        // 監聽值
        watchedEnabled,

        // 載入狀態
        isLoadingKioskSetting,
        isUpdatingKioskSetting,
    };
};
